import React, { useEffect, useState } from 'react';
import type { IGetManagedAccessKdpDetailsResponse } from '@/app-package';
import { buildGoogleMapsAddress } from 'utils';
import { ListingMapView, Avatar, ListingEntry } from 'components';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Alert,
  Icon,
  Link,
  Text,
  Space,
  Flex,
  Spin,
  message,
  Color,
} from '@bb/elements-web';
import { getManagedAccessKdpDetails } from 'api/SupraController';
import { DetailRow } from './components';
import { Section } from '../ShowingDetailsPage/components';

/** The managed access details page to display details for a specific managed access KDP */
const ManagedAccessDetailsPage: React.FC = () => {
  const { managedAccessKdpId } = useParams<{ managedAccessKdpId: string }>();
  const [managedAccessKdpDetails, setManagedAccessKdpDetails] =
    useState<IGetManagedAccessKdpDetailsResponse | null>(null);

  /** Redirect to google maps when clicking direction */
  const clickDirections: React.MouseEventHandler<HTMLButtonElement> = event => {
    if (
      !managedAccessKdpDetails ||
      !managedAccessKdpDetails.listing ||
      !managedAccessKdpDetails.listing.line1 ||
      !managedAccessKdpDetails.listing.country ||
      !managedAccessKdpDetails.listing.province ||
      !managedAccessKdpDetails.listing.postal_code ||
      !managedAccessKdpDetails.listing.city
    ) {
      message.error(
        'The listing address details are incomplete. Cannot get directions.',
      );
      return;
    }

    event.stopPropagation();
    const destination = buildGoogleMapsAddress(
      managedAccessKdpDetails?.listing as any,
    );

    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${destination}`,
      '_blank',
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getManagedAccessKdpDetails({
          id: managedAccessKdpId,
        });
        setManagedAccessKdpDetails(data);
      } catch (err) {
        message.error('Error fetching managed access details');
      }
    };
    fetchData();
  }, [managedAccessKdpId]);

  return managedAccessKdpDetails ? (
    <Layout>
      <ListingMapView
        coordinates={
          managedAccessKdpDetails?.listing?.coords?.coordinates as [
            number,
            number,
          ]
        }
      />

      <AlertContainer>
        <Alert
          type="success"
          banner
          message="Managed Access"
          showIcon
          style={{ height: '39px' }}
          icon={
            <Flex justify="center" align="center">
              <Icon size={20} icon="padlockCircledChecked" color="green" />
            </Flex>
          }
        />
      </AlertContainer>

      <Space size={32} direction="vertical">
        <ListingEntry
          listingId={managedAccessKdpDetails?.listing.id}
          time={managedAccessKdpDetails?.startDateTime}
          timeEnd={managedAccessKdpDetails?.endDateTime}
          timezone={managedAccessKdpDetails?.listing?.timezone}
          showTimezone
          line1={managedAccessKdpDetails?.listing?.line1}
          line2={managedAccessKdpDetails?.listing?.line2}
          thumbnail={managedAccessKdpDetails?.listing?.thumbnail}
          mlsNumber={managedAccessKdpDetails?.listing?.mls_number}
          type="showing"
        />

        <Section title="Details">
          <Space size={32} direction="vertical">
            <DetailRow icon="directions">
              <Link
                dataCy="managed-access-directions-link"
                onClick={clickDirections}
              >
                Get directions
              </Link>
            </DetailRow>

            <DetailRow icon="userHome">
              <Text>Managed Access</Text>
            </DetailRow>
          </Space>
        </Section>

        <Section title="Grantor">
          <Space size={24} direction="vertical">
            <DetailRow icon="userRectangle">
              <Space size={8} direction="horizontal">
                <Avatar
                  backgroundColor="gray3"
                  color="black"
                  user={{
                    firstName: managedAccessKdpDetails?.grantorUser?.firstName,
                    lastName: managedAccessKdpDetails?.grantorUser?.lastName,
                    avatarFilestackId:
                      managedAccessKdpDetails?.grantorUser?.avatarFilestackId,
                  }}
                />
                <Flex vertical>
                  <Text style={{ lineHeight: '22px' }}>
                    {managedAccessKdpDetails?.grantorUser?.firstName}{' '}
                    {managedAccessKdpDetails?.grantorUser?.lastName}
                  </Text>
                  <Text
                    color="BODY_TEXT"
                    size="small"
                    style={{ lineHeight: '20px' }}
                  >
                    {managedAccessKdpDetails?.grantorUser?.title}
                  </Text>
                </Flex>
              </Space>
            </DetailRow>

            <DetailRow icon="building">
              <Space size={8} direction="horizontal">
                <Avatar src={managedAccessKdpDetails?.org?.logoUrl} />
                <Text>{managedAccessKdpDetails?.org?.name}</Text>
              </Space>
            </DetailRow>
          </Space>
        </Section>

        <Section title="Access">
          <Space size={24} direction="vertical">
            <DetailRow icon="lockOutline">
              <Text>Use the Supra One app during appointment hours</Text>
            </DetailRow>
          </Space>
        </Section>
      </Space>
    </Layout>
  ) : (
    <Layout>
      <Spin dataCy="managed-access-details-loading" />
    </Layout>
  );
};

export default ManagedAccessDetailsPage;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin: auto;
  padding: 32px;
  width: 90%;
  background: ${Color.WHITE};
`;

const AlertContainer = styled.div`
  margin-bottom: 16px;
`;
